import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api.ts'
import {
  Monitor, AlertTriangle, Play, Wifi, WifiOff,
  Filter, ChevronDown, ChevronUp, Server,
} from 'lucide-react'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import {
  Button,
  Card,
  DataTable,
  FilterBar,
  IconButton,
  Input,
  Modal,
  PageHeader,
  Select,
  StatusBadge,
  actionsColumn,
  dateColumn,
  textColumn,
  useToast,
  type ColumnDef,
} from '@/components/ui'

interface CwaComputer {
  id: number
  name: string
  domain?: string
  os?: string
  osVersion?: string
  lastContact?: string
  online: boolean
  clientId?: number
  clientName?: string
  locationName?: string
  agentVersion?: string
  cpu?: string
  ram?: number
}

interface CwaClient {
  id: number
  name: string
}

interface CwaAlert {
  id: number
  alertType: string
  severity: string
  message: string
  computerName?: string
  computerId?: number
  clientName?: string
  createdAt: string
  acknowledged: boolean
}

interface StatCardProps {
  label: string
  value: string | number
  icon: typeof Monitor
  iconBg: string
  iconColor: string
}

function StatCard({ label, value, icon: Icon, iconBg, iconColor }: StatCardProps) {
  return (
    <Card>
      <Card.Body className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${iconBg}`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-caption text-gray-500">{label}</p>
          <p className="text-h3 text-gray-900">{value}</p>
        </div>
      </Card.Body>
    </Card>
  )
}

export function CwaOverviewPage() {
  const a = useAnonymize()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [clientFilter, setClientFilter] = useState<number | undefined>()
  const [statusFilter, setStatusFilter] = useState<'online' | 'offline' | ''>('')
  const [scriptId, setScriptId] = useState('')
  const [scriptTarget, setScriptTarget] = useState<CwaComputer | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  const { data: clients } = useQuery<CwaClient[]>({
    queryKey: ['cwa-clients'],
    queryFn: () => api.get('/cwa/clients').then((r) => r.data),
    retry: false,
  })

  const { data: computers, isLoading: computersLoading, error: computersError, refetch: refetchComputers } =
    useQuery<CwaComputer[]>({
      queryKey: ['cwa-computers', clientFilter, statusFilter],
      queryFn: () => {
        const params = new URLSearchParams()
        if (clientFilter) params.set('clientId', String(clientFilter))
        if (statusFilter) params.set('status', statusFilter)
        const qs = params.toString()
        return api.get(`/cwa/computers${qs ? `?${qs}` : ''}`).then((r) => r.data)
      },
      refetchInterval: 60_000,
      retry: false,
    })

  const { data: alerts, isLoading: alertsLoading, error: alertsError, refetch: refetchAlerts } =
    useQuery<CwaAlert[]>({
      queryKey: ['cwa-alerts'],
      queryFn: () => api.get('/cwa/alerts?acknowledged=false').then((r) => r.data),
      refetchInterval: 60_000,
      retry: false,
    })

  const runScriptMutation = useMutation({
    mutationFn: ({ scriptId, computerId }: { scriptId: number; computerId: number }) =>
      api.post(`/cwa/scripts/${scriptId}/run`, { computerId }).then((r) => r.data),
    onSuccess: (data) => {
      toast({
        title: 'Script gestartet',
        description: `Job-ID: ${data.jobId}`,
        tone: 'success',
      })
      setScriptTarget(null)
      setScriptId('')
      queryClient.invalidateQueries({ queryKey: ['cwa-computers'] })
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler'
      toast({ title: 'Script konnte nicht gestartet werden', description: message, tone: 'danger' })
    },
  })

  const onlineCount = computers?.filter((c) => c.online).length ?? 0
  const offlineCount = computers?.filter((c) => !c.online).length ?? 0
  const hasActiveFilters = clientFilter != null || statusFilter !== ''

  const computerColumns = useMemo<ColumnDef<CwaComputer, unknown>[]>(
    () => [
      {
        accessorKey: 'online',
        header: 'Status',
        size: 110,
        cell: ({ row }) => <StatusBadge status={row.original.online ? 'online' : 'offline'} />,
      },
      {
        accessorKey: 'name',
        header: 'Computer',
        cell: ({ row }) => (
          <div className="flex flex-col min-w-0">
            <span className="text-body font-medium text-gray-900 truncate">
              {a.token(row.original.name)}
            </span>
            {row.original.domain ? (
              <span className="text-caption text-gray-500 truncate">{row.original.domain}</span>
            ) : null}
          </div>
        ),
      },
      {
        accessorKey: 'clientName',
        header: 'Kunde',
        cell: ({ row }) =>
          row.original.clientName ? a.name(row.original.clientName) : '—',
      },
      textColumn<CwaComputer>('os', 'OS'),
      dateColumn<CwaComputer>('lastContact', 'Zuletzt', { withTime: true, size: 160 }),
      actionsColumn<CwaComputer>(
        '',
        (computer) => (
          <IconButton
            icon={<Play />}
            aria-label="Script ausführen"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              setScriptTarget(computer)
            }}
          />
        ),
        { size: 60 },
      ),
    ],
    [a],
  )

  const alertColumns = useMemo<ColumnDef<CwaAlert, unknown>[]>(
    () => [
      {
        accessorKey: 'severity',
        header: 'Severity',
        size: 110,
        cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
      },
      {
        accessorKey: 'message',
        header: 'Meldung',
        cell: ({ row }) => (
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle
              className={
                row.original.severity.toLowerCase() === 'critical'
                  ? 'w-4 h-4 text-red-500 flex-shrink-0 mt-0.5'
                  : 'w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5'
              }
            />
            <div className="min-w-0">
              <p className="text-body text-gray-900 truncate">{a.freeText(row.original.message)}</p>
              <div className="flex items-center gap-2 text-caption text-gray-500 mt-0.5">
                {row.original.computerName ? <span>{a.token(row.original.computerName)}</span> : null}
                {row.original.clientName ? <span>{a.name(row.original.clientName)}</span> : null}
              </div>
            </div>
          </div>
        ),
      },
      dateColumn<CwaAlert>('createdAt', 'Zeit', { withTime: true, size: 150 }),
    ],
    [a],
  )

  const clientOptions = useMemo(
    () => [
      { value: '', label: 'Alle Kunden' },
      ...(clients ?? []).map((c) => ({ value: String(c.id), label: a.name(c.name) })),
    ],
    [clients, a],
  )

  const statusOptions = [
    { value: '', label: 'Alle' },
    { value: 'online', label: 'Online' },
    { value: 'offline', label: 'Offline' },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="ConnectWise Automate"
        description="RMM-Verwaltung und Monitoring"
        actions={
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Filter className="h-4 w-4" />}
            trailingIcon={
              showFilters ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )
            }
            onClick={() => setShowFilters((v) => !v)}
          >
            Filter
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label="Gesamte Computer"
          value={computersLoading ? '…' : (computers?.length ?? 0)}
          icon={Monitor}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
        />
        <StatCard
          label="Online"
          value={computersLoading ? '…' : onlineCount}
          icon={Wifi}
          iconBg="bg-green-50"
          iconColor="text-green-500"
        />
        <StatCard
          label="Offline"
          value={computersLoading ? '…' : offlineCount}
          icon={WifiOff}
          iconBg={offlineCount > 0 ? 'bg-red-50' : 'bg-gray-50'}
          iconColor={offlineCount > 0 ? 'text-red-500' : 'text-gray-400'}
        />
      </div>

      {showFilters ? (
        <FilterBar>
          <Select
            label="Kunde"
            value={clientFilter != null ? String(clientFilter) : ''}
            onValueChange={(v) => setClientFilter(v ? Number(v) : undefined)}
            options={clientOptions}
            wrapperClassName="w-56"
          />
          <Select
            label="Status"
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as 'online' | 'offline' | '')}
            options={statusOptions}
            wrapperClassName="w-40"
          />
          {hasActiveFilters ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setClientFilter(undefined)
                setStatusFilter('')
              }}
              className="self-end"
            >
              Zurücksetzen
            </Button>
          ) : null}
        </FilterBar>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <DataTable<CwaComputer>
            data={computers ?? []}
            columns={computerColumns}
            getRowId={(row) => String(row.id)}
            loading={computersLoading}
            error={computersError ? (computersError as Error).message : undefined}
            onRetry={() => refetchComputers()}
            pagination={{ pageSize: 25 }}
            toolbar={
              <div className="flex items-center gap-2 text-small text-gray-600">
                <Server className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-gray-800">Computer</span>
                <span className="text-caption text-gray-500">
                  · {computers?.length ?? 0} Einträge
                </span>
              </div>
            }
            emptyState={undefined}
          />
        </div>

        <div>
          <DataTable<CwaAlert>
            data={alerts ?? []}
            columns={alertColumns}
            getRowId={(row) => String(row.id)}
            loading={alertsLoading}
            error={alertsError ? (alertsError as Error).message : undefined}
            onRetry={() => refetchAlerts()}
            toolbar={
              <div className="flex items-center gap-2 text-small text-gray-600">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="font-medium text-gray-800">Aktive Alerts</span>
                <span className="text-caption text-gray-500">
                  · {alerts?.length ?? 0}
                </span>
              </div>
            }
          />
        </div>
      </div>

      <Modal
        open={scriptTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setScriptTarget(null)
            setScriptId('')
          }
        }}
        title="Script ausführen"
        description={
          scriptTarget
            ? `Auf ${a.token(scriptTarget.name)}${
                scriptTarget.clientName ? ` (${a.name(scriptTarget.clientName)})` : ''
              }`
            : undefined
        }
        size="sm"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setScriptTarget(null)
                setScriptId('')
              }}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              loading={runScriptMutation.isPending}
              disabled={!scriptId}
              onClick={() => {
                if (scriptTarget && scriptId) {
                  runScriptMutation.mutate({
                    scriptId: Number(scriptId),
                    computerId: scriptTarget.id,
                  })
                }
              }}
            >
              Starten
            </Button>
          </>
        }
      >
        <Input
          type="number"
          label="Script-ID"
          placeholder="z.B. 1234"
          value={scriptId}
          onChange={(e) => setScriptId(e.currentTarget.value)}
          autoFocus
        />
      </Modal>
    </div>
  )
}
